/* eslint-disable @typescript-eslint/no-explicit-any */
import { NoteUserMetadata } from '@eigtech/notes-types'
import { Corn } from '@eigtech/shared-corn'
import { useCohortContext } from '@eigtech/ui-shared-api'
import { Button, ComposedAlert, ComposedCard, Stack } from '@eigtech/ui-shared-dave'
import { createForm } from '@eigtech/ui-shared-forms'
import { useCreateEntityNote, useNotesContext } from '@eigtech/ui-shared-notes'
import { ReactNode } from 'react'
import { makeAddNotePayload } from '../utils/makeAddNotePayload'
import { NoteFields } from './NoteFields'
import { NoteFieldsSchema, NoteFieldsType } from './NoteFieldsSchema'
import { NoteTemplateField } from './NoteTemplateField'
import { PublishNoteField } from './PublishNoteField'

export type CreateEntityNotePageProps = {
  entityCorn: Corn
  noteText?: string
  noteTitle?: string
  templateParams?: Record<string, unknown>
  title?: ReactNode
  includeTemplateField?: boolean
  includePublishField?: boolean
  userMetadata?: NoteUserMetadata
  onSuccess?: () => any
}

export function CreateEntityNotePage({
  entityCorn,
  noteText,
  noteTitle,
  templateParams,
  includePublishField = true,
  includeTemplateField = true,
  userMetadata,
  onSuccess,
}: CreateEntityNotePageProps) {
  const { cohort } = useCohortContext()
  const { dataSource, metadata } = useNotesContext()

  const form = useForm({
    defaultValues: {
      title: noteTitle ?? '',
      text: noteText ?? '',
    },
  })

  const { mutateAsync: createNote, isError } = useCreateEntityNote(entityCorn)

  async function onSubmit(data: NoteFieldsType) {
    const payload = makeAddNotePayload({ ...data, cohort, entityCorn, source: dataSource })

    await createNote({
      ...payload,
      userMetadata: {
        ...metadata,
        ...userMetadata,
      },
    })

    onSuccess?.()
  }

  return (
    <ComposedCard
      footer={!form.formState.isSubmitSuccessful && <form.SubmitButton />}
      heading="Create Note"
    >
      {form.formState.isSubmitSuccessful ? (
        <Stack alignItems="center" maxW="container.sm" mx="auto">
          <ComposedAlert alert="Successfully added note!" status="success" />
          <Button flexShrink={0} onClick={() => location.reload()}>
            Create New Note
          </Button>
        </Stack>
      ) : (
        <form.Form onSubmit={onSubmit}>
          {includeTemplateField && <NoteTemplateField templateParams={templateParams} />}

          <NoteFields entityCorn={entityCorn} />

          {includePublishField && <PublishNoteField />}

          {isError && (
            <ComposedAlert
              alert={{
                title: 'Error!',
                description: 'Something went wrong when creating the note.',
              }}
              status="error"
            />
          )}
        </form.Form>
      )}
    </ComposedCard>
  )
}

const { useForm } = createForm(NoteFieldsSchema, 'createNoteForm')
